import {Exchange} from "app/instruments/exchange/Exchange";
import {OffsetDateTime} from "../../pseudo-types/DateTypes";
import {QuoteParameters, TmcQuoteParameters} from "./QuoteParameters";
import {DateTimeUtil} from "../../js/util/DateTimeUtil";
import {QuoteRequest} from "./QuoteRequest";

export type QuoteRecords = ReadonlyArray<QuoteRecord>;

export interface QuoteRecord extends QuoteParameters {
    readonly asAt: OffsetDateTime;
    readonly quoteStatus: QuoteStatus;
    readonly bid: number | null;
    readonly ask: number | null;
    readonly mid: number;
    readonly prevClose: number;
    readonly open: number;
    readonly high: number;
    readonly low: number;
    readonly change: number;
    readonly changePct: number;
    readonly last: number | null;
    readonly matchPrice: number | null;
    readonly matchVolume: number;
    readonly basisOfQuotation: BasisOfQuotations;
    readonly tradingState: TradingState;
    readonly bidVolume: number;
    readonly askVolume: number;
    readonly priceBasis: PriceBasis;
    readonly tradeCount: number;
    readonly totalTradedValue: number;
    readonly totalTradedVolume: number;
}

export enum QuoteStatus {
    OK = "OK",
    FAILED = "FAILED"
}

export enum TradingState {
    OPEN = "OPEN",
    DASH = "DASH",
    ADJUST_ON = "ADJUST_ON",
    PRE_OPEN = "PRE_OPEN",
    SUSPENDED = "SUSPENDED",
    CLOSED = "CLOSED",
    HALT = "HALT",
    PRE_NR = "PRE_NR",
    PRE_CSPA = "PRE_CSPA",
    OPENING = "OPENING",
    INQUIRY_ONLY = "INQUIRY_ONLY",
    INQUIRY = "INQUIRY",
    ADJUST = "ADJUST",
    REG_HALT = "REG_HALT",
    WAIT = "WAIT",
    MARKET_ON_CLOSE = "MARKET_ON_CLOSE"
}

export type BasisOfQuotations = ReadonlyArray<BasisOfQuotation>;

export enum BasisOfQuotation {
    CALL_DUE = "CALL_DUE",
    CALL_PAID = "CALL_PAID",
    CONDITIONAL_TRADING = "CONDITIONAL_TRADING",
    CUM_BONUS_ISSUE = "CUM_BONUS_ISSUE",
    EX_BONUS_ISSUE = "EX_BONUS_ISSUE",
    CUM_CAPITAL_RETURN = "CUM_CAPITAL_RETURN",
    EX_CAPITAL_RETURN = "EX_CAPITAL_RETURN",
    CUM_DIVIDEND = "CUM_DIVIDEND",
    EX_DIVIDEND = "EX_DIVIDEND",
    CUM_ENTITLEMENT = "CUM_ENTITLEMENT",
    EX_ENTITLEMENT = "EX_ENTITLEMENT",
    CUM_EQUAL_ACCESS_SCHEME = "CUM_EQUAL_ACCESS_SCHEME",
    EX_EQUAL_ACCESS_SCHEME = "EX_EQUAL_ACCESS_SCHEME",
    CUM_INTEREST = "CUM_INTEREST",
    EX_INTEREST = "EX_INTEREST",
    CUM_PRIORITY = "CUM_PRIORITY",
    EX_PRIORITY = "EX_PRIORITY",
    CUM_RIGHTS_ISSUE = "CUM_RIGHTS_ISSUE",
    EX_RIGHTS_ISSUE = "EX_RIGHTS_ISSUE",
    CUM_TAKEOVER_OFFER = "CUM_TAKEOVER_OFFER",
    EX_TAKEOVER_OFFER = "EX_TAKEOVER_OFFER",
    NEW_EX_INTEREST = "NEW_EX_INTEREST",
    PROTECTION_AVAILABLE = "PROTECTION_AVAILABLE",
    PROTECTION_UNAVAILABLE = "PROTECTION_UNAVAILABLE",
    RECONSTRUCTED = "RECONSTRUCTED"
}

export enum PriceBasis {
    POINTS = "POINTS",
    CENTS = "CENTS",
    DOLLARS = "DOLLARS"
}

export type UnderlyingQuoteRecord = QuoteRecord;
export type WarrantQuoteRecord = QuoteRecord;

export interface OptionQuoteRecord extends QuoteRecord {
    readonly underlyingPrice: number;
    readonly theo: number;
    readonly delta: number;
    readonly vol: number;
    readonly bidLimit: number;
    readonly askLimit: number;
}

export interface TmcQuoteRecord extends OptionQuoteRecord, TmcQuoteParameters {
    readonly legs: ReadonlyArray<UnderlyingQuoteRecord | OptionQuoteRecord>;
    readonly opposite: boolean;
}

export function isUnderlyingQuote(quote: QuoteRecord): quote is UnderlyingQuoteRecord {
    return (quote.exchange === Exchange.ASX) ||
        (quote.exchange === Exchange.NSX) ||
        // TODO needs a better check.. somehow
        ((quote.exchange === Exchange.CXA) && (quote.code.length === 3));
}

export function isWarrantQuote(quote: QuoteRecord): quote is UnderlyingQuoteRecord {
    // TODO Temp code, plz fix
    return (quote.exchange === Exchange.AXW) || ((quote.exchange === Exchange.CXA) && (quote.code.length === 6));
}

export function isOptionQuote(quote: QuoteRecord): quote is OptionQuoteRecord {
    return (quote.exchange === Exchange.AOM) && !isTmcQuote(quote);
}

export function isTmcQuote(quote: QuoteRecord): quote is TmcQuoteRecord {
    return "legs" in quote;
}

export function missingQuote(quoteRequest: QuoteRequest): QuoteRecord {
    return {
        asAt: DateTimeUtil.offsetNow(),
        portfolioType: quoteRequest.portfolioType,
        quoteStatus: QuoteStatus.FAILED,
        code: quoteRequest.code,
        exchange: quoteRequest.exchange,
        bidVolume: 0,
        askVolume: 0,
        bid: null,
        ask: null,
        last: null,
        matchPrice: null,
        matchVolume: 0,
        basisOfQuotation: [],
        change: 0,
        changePct: 0,
        mid: 0,
        open: 0,
        high: 0,
        low: 0,
        prevClose: 0,
        priceBasis: PriceBasis.DOLLARS,
        tradingState: TradingState.OPEN,
        tradeCount: 0,
        totalTradedValue: 0,
        totalTradedVolume: 0
    };
}

export function isMarketOpenState(state: TradingState): boolean {
    return state !== TradingState.CLOSED;
}
